/**
 * @fileoverview Filters bar component for filtering storage units
 * @module ctl-filters-bar
 * @version 1.0.0
 * 
 * @description
 * A comprehensive filters bar for filtering storage units by size, features, 
 * price range, and promotions. Designed for storefront variant.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-filters-bar></ctl-filters-bar>
 * 
 * @example
 * <!-- With initial values -->
 * <ctl-filters-bar 
 *   value='{"size": "5x10", "climate": true, "priceRange": [50, 200]}'
 * ></ctl-filters-bar>
 */

import "../forms/ctl-select.js";

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-4);
    padding: var(--ctl-space-5);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-12);
    background: var(--ctl-color-surface);
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-4);
  }

  .title {
    font-size: var(--ctl-font-size-base);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-text);
    margin: 0;
  }

  .reset-button {
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1);
    padding: var(--ctl-space-1-5) var(--ctl-space-3);
    border: 1px solid var(--ctl-color-border);
    background: transparent;
    color: var(--ctl-color-muted);
    border-radius: var(--ctl-radius-md);
    font-family: inherit;
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-normal);
    cursor: pointer;
    transition: background .15s ease, border-color .15s ease, color .15s ease;
  }

  .reset-button:hover {
    background: var(--ctl-color-overlay-1);
    border-color: var(--ctl-color-text);
    color: var(--ctl-color-text);
  }

  .filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--ctl-space-4);
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2);
  }

  .filter-label {
    font-size: var(--ctl-font-size-base);
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-text);
  }

  .checkboxes {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-2-5);
  }

  .checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
  }

  .checkbox-wrapper input[type="checkbox"] {
    width: var(--ctl-space-4-5);
    height: var(--ctl-space-4-5);
    cursor: pointer;
    accent-color: var(--ctl-color-brand);
  }

  .checkbox-wrapper label {
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-text);
    cursor: pointer;
    user-select: none;
  }

  .slider-section {
    grid-column: 1 / -1;
  }

  .price-range-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-3);
  }

  .price-inputs {
    display: flex;
    gap: var(--ctl-space-3);
    align-items: center;
  }

  .price-input-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
  }

  .price-input-label {
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-muted);
  }

  .price-input {
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-color-surface);
    font-family: inherit;
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-text);
  }

  .price-input:focus {
    outline: none;
    border-color: var(--ctl-color-brand);
    box-shadow: 0 0 0 var(--ctl-space-0-5) color-mix(in srgb, var(--ctl-color-brand) 15%, transparent);
  }

  .price-separator {
    color: var(--ctl-color-muted);
    padding-top: var(--ctl-space-5);
  }

  /* Variant: compact */
  :host([variant="compact"]) .container {
    padding: var(--ctl-space-4);
  }

  :host([variant="compact"]) .filters-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: var(--ctl-space-3);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .filters-grid {
      grid-template-columns: 1fr;
    }
  }
</style>

<div class="container" part="container">
  <div class="header" part="header">
    <h3 class="title" part="title">Filters</h3>
    <button class="reset-button" part="reset-button">Reset all</button>
  </div>

  <div class="filters-grid" part="filters-grid">
    <div class="filter-group" part="filter-group">
      <label class="filter-label" part="filter-label">Unit Size</label>
      <ctl-select id="sizeSelect" part="size-select"></ctl-select>
    </div>

    <div class="filter-group" part="filter-group">
      <label class="filter-label" part="filter-label">Features</label>
      <div class="checkboxes" part="checkboxes">
        <div class="checkbox-wrapper">
          <input type="checkbox" id="climate" />
          <label for="climate">Climate controlled</label>
        </div>
        <div class="checkbox-wrapper">
          <input type="checkbox" id="driveup" />
          <label for="driveup">Drive-up access</label>
        </div>
        <div class="checkbox-wrapper">
          <input type="checkbox" id="indoor" />
          <label for="indoor">Indoor</label>
        </div>
      </div>
    </div>

    <div class="filter-group" part="filter-group">
      <label class="filter-label" part="filter-label">Access</label>
      <div class="checkboxes" part="checkboxes">
        <div class="checkbox-wrapper">
          <input type="checkbox" id="elevator" />
          <label for="elevator">Elevator access</label>
        </div>
      </div>
      <label class="filter-label" part="filter-label" style="margin-top: var(--ctl-space-2);">Floor Level</label>
      <ctl-select id="floorSelect" part="floor-select"></ctl-select>
    </div>

    <div class="filter-group" part="filter-group">
      <label class="filter-label" part="filter-label">Special Offers</label>
      <div class="checkboxes" part="checkboxes">
        <div class="checkbox-wrapper">
          <input type="checkbox" id="promotions" />
          <label for="promotions">Promotions only</label>
        </div>
      </div>
    </div>

    <div class="filter-group slider-section" part="filter-group">
      <label class="filter-label" part="filter-label">Price Range</label>
      <div class="price-range-wrapper" part="price-range-wrapper">
        <div class="price-inputs" part="price-inputs">
          <div class="price-input-wrapper">
            <span class="price-input-label">Min</span>
            <input type="number" id="minPrice" class="price-input" min="0" step="10" placeholder="$0" />
          </div>
          <span class="price-separator">—</span>
          <div class="price-input-wrapper">
            <span class="price-input-label">Max</span>
            <input type="number" id="maxPrice" class="price-input" min="0" step="10" placeholder="$500" />
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
`;

function safeJsonParse(str, fallback) {
  try { return JSON.parse(str); } catch { return fallback; }
}

/**
 * Filters Bar Web Component
 * 
 * @class CtlFiltersBar
 * @extends HTMLElement
 * 
 * @attr {string} value - JSON object with current filter values
 * @attr {string} variant - Display variant (compact)
 * @attr {boolean} disabled - Whether the filters are disabled
 * 
 * @fires ctl-change - Dispatched when any filter changes
 * @fires ctl-change.detail.value - Current filter values
 * @fires ctl-reset - Dispatched when filters are reset
 * 
 * @csspart container - The main container
 * @csspart header - The header section
 * @csspart title - The title element
 * @csspart reset-button - The reset button
 * @csspart filters-grid - The filters grid container
 * @csspart filter-group - Each filter group
 * @csspart filter-label - Filter labels
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-brand - Brand color
 */
export class CtlFiltersBar extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._sizeSelect = this.shadowRoot.getElementById('sizeSelect');
    this._floorSelect = this.shadowRoot.getElementById('floorSelect');
    this._climate = this.shadowRoot.getElementById('climate');
    this._driveup = this.shadowRoot.getElementById('driveup');
    this._indoor = this.shadowRoot.getElementById('indoor');
    this._elevator = this.shadowRoot.getElementById('elevator');
    this._promotions = this.shadowRoot.getElementById('promotions');
    this._minPrice = this.shadowRoot.getElementById('minPrice');
    this._maxPrice = this.shadowRoot.getElementById('maxPrice');
    this._resetButton = this.shadowRoot.querySelector('.reset-button');

    this._sizeOptions = [
      { value: '', label: 'Any size' },
      { value: '5x5', label: '5×5 (25 sq ft)' },
      { value: '5x10', label: '5×10 (50 sq ft)' },
      { value: '10x10', label: '10×10 (100 sq ft)' },
      { value: '10x15', label: '10×15 (150 sq ft)' },
      { value: '10x20', label: '10×20 (200 sq ft)' },
      { value: '10x30', label: '10×30 (300 sq ft)' }
    ];

    this._floorOptions = [
      { value: '', label: 'Any floor' },
      { value: 'ground', label: 'Ground floor' },
      { value: 'upper', label: 'Upper floor' }
    ];
    
    // Bind handlers once
    this._boundHandleReset = this._handleReset.bind(this);
    this._boundHandleChange = this._handleChange.bind(this);
    this._boundHandleInput = this._handleInput.bind(this);
  }

  static get observedAttributes() {
    return ['value', 'variant', 'disabled'];
  }

  connectedCallback() {
    // Initialize selects
    this._sizeSelect.setAttribute('options', JSON.stringify(this._sizeOptions));
    this._floorSelect.setAttribute('options', JSON.stringify(this._floorOptions));

    // Event listeners
    this._resetButton.addEventListener('click', this._boundHandleReset);
    this.shadowRoot.addEventListener('change', this._boundHandleChange);
    this.shadowRoot.addEventListener('input', this._boundHandleInput);
    this.shadowRoot.addEventListener('ctl-change', this._boundHandleChange);

    this._applyValue();
    this._syncDisabled();
  }

  disconnectedCallback() {
    this._resetButton.removeEventListener('click', this._boundHandleReset);
    this.shadowRoot.removeEventListener('change', this._boundHandleChange);
    this.shadowRoot.removeEventListener('input', this._boundHandleInput);
    this.shadowRoot.removeEventListener('ctl-change', this._boundHandleChange);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'value') {
        this._applyValue();
      } else if (name === 'disabled') {
        this._syncDisabled();
      }
    }
  }

  _applyValue() {
    const value = safeJsonParse(this.getAttribute('value'), {});
    
    if (value.size !== undefined) {
      this._sizeSelect.value = value.size;
      this._sizeSelect.setAttribute('value', value.size);
    }
    if (value.floor !== undefined) {
      this._floorSelect.value = value.floor;
      this._floorSelect.setAttribute('value', value.floor);
    }
    if (value.climate !== undefined) this._climate.checked = value.climate;
    if (value.driveup !== undefined) this._driveup.checked = value.driveup;
    if (value.indoor !== undefined) this._indoor.checked = value.indoor;
    if (value.elevator !== undefined) this._elevator.checked = value.elevator;
    if (value.promotions !== undefined) this._promotions.checked = value.promotions;
    if (value.minPrice != null) this._minPrice.value = value.minPrice;
    if (value.maxPrice != null) this._maxPrice.value = value.maxPrice;
  }
  
  _syncDisabled() {
    const disabled = this.hasAttribute('disabled');
    
    // Disable all inputs
    this._sizeSelect.toggleAttribute('disabled', disabled);
    this._floorSelect.toggleAttribute('disabled', disabled);
    this._climate.disabled = disabled;
    this._driveup.disabled = disabled;
    this._indoor.disabled = disabled;
    this._elevator.disabled = disabled;
    this._promotions.disabled = disabled;
    this._minPrice.disabled = disabled;
    this._maxPrice.disabled = disabled;
    this._resetButton.disabled = disabled;
  }

  _getValue() {
    return {
      size: this._sizeSelect.value || '',
      floor: this._floorSelect.value || '',
      climate: this._climate.checked,
      driveup: this._driveup.checked,
      indoor: this._indoor.checked,
      elevator: this._elevator.checked,
      promotions: this._promotions.checked,
      minPrice: this._minPrice.value ? parseFloat(this._minPrice.value) : null,
      maxPrice: this._maxPrice.value ? parseFloat(this._maxPrice.value) : null
    };
  }

  _handleChange(e) {
    // Prevent infinite loops
    if (e.type === 'ctl-change' && e.target === this) return;
    
    // Ignore changes when disabled
    if (this.hasAttribute('disabled')) return;

    const value = this._getValue();
    this.setAttribute('value', JSON.stringify(value));
    
    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: { value }
    }));
  }

  _handleInput(e) {
    // Handle real-time price input changes
    if (e.target === this._minPrice || e.target === this._maxPrice) {
      this._handleChange(e);
    }
  }

  _handleReset() {
    if (this.hasAttribute('disabled')) return;
    this._sizeSelect.value = '';
    this._sizeSelect.setAttribute('value', '');
    this._floorSelect.value = '';
    this._floorSelect.setAttribute('value', '');
    this._climate.checked = false;
    this._driveup.checked = false;
    this._indoor.checked = false;
    this._elevator.checked = false;
    this._promotions.checked = false;
    this._minPrice.value = '';
    this._maxPrice.value = '';

    const value = this._getValue();
    this.setAttribute('value', JSON.stringify(value));
    
    this.dispatchEvent(new CustomEvent('ctl-reset', {
      bubbles: true,
      composed: true,
      detail: { value }
    }));

    this.dispatchEvent(new CustomEvent('ctl-change', {
      bubbles: true,
      composed: true,
      detail: { value }
    }));
  }

  /**
   * Get the current filter values
   * @returns {Object} The current filter values
   */
  get value() {
    return this._getValue();
  }

  /**
   * Set the filter values
   * @param {Object} val - Filter values object
   */
  set value(val) {
    this.setAttribute('value', JSON.stringify(val));
  }

  /**
   * Reset all filters
   */
  reset() {
    this._handleReset();
  }
}

if (!customElements.get("ctl-filters-bar")) {
  customElements.define("ctl-filters-bar", CtlFiltersBar);
}
